import type { SubPortfolio } from '@festina/unitlink-general-types/unitlink'
import {
  assertNotEmpty,
  notEmpty,
  typeUtils,
  type ValidatedKey,
} from '@festina-life/core'
import { FormikSelect } from '@festina-life/ui/formikForms'
import { useField } from 'formik'
import { useMemo } from 'react'

import { useComponent, useHook } from '../../../lenteHooks'

type Props = {
  fieldNamePrefix: ValidatedKey
}

export const SubPortfolioFields = ({ fieldNamePrefix }: Props) => {
  // The generated fields omit the text input for subPortfolioKey
  // so that we can add it ourselves as a Select with extra logic.
  const SubPortfolioRemainingFields = useComponent(
    'pages.portfolioEditor.SubPortfolioFields',
  )
  assertNotEmpty(SubPortfolioRemainingFields)

  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data } = useHook(
    'fetchers.unitlink.useFetchPortfolioLatestForUseInEditPortfolio',
  )({ tenantId, ownerId })

  const subPortfolioMap = useMemo(
    () =>
      Object.fromEntries(
        data?.subPortfolioGroups
          ?.flatMap((subPortfolioGroup) =>
            subPortfolioGroup?.subPortfolios?.flatMap((subPortfolio) =>
              subPortfolio?.subPortfolioKey
                ? [[subPortfolio.subPortfolioKey, subPortfolio] as const]
                : [],
            ),
          )
          .filter(notEmpty) ?? [],
      ),
    [data?.subPortfolioGroups],
  )

  const [, , { setValue: setSubPortfolio }] = useField(fieldNamePrefix)

  const validateKey =
    typeUtils.createNestedValidator<SubPortfolio>(fieldNamePrefix)

  return (
    <>
      <FormikSelect
        // @beta translations are omitted because this is a raw command and all other generated fields are untranslated
        label="Sub portfolio key"
        name={validateKey('subPortfolioKey')}
        description="A key identifying the sub portfolio. This key can be used to fetch the sub portfolio's balance accounts which are placed on the PortfolioSource."
        options={Object.keys(subPortfolioMap)}
        onBlur={async (option: string | undefined) => {
          if (option && option in subPortfolioMap) {
            // This includes setting `_id`s, which according to @jlj, should be sent as-is but not shown.
            await setSubPortfolio(subPortfolioMap[option])
          }
        }}
      />
      <SubPortfolioRemainingFields fieldNamePrefix={fieldNamePrefix} />
    </>
  )
}
